import { useState, useRef, useLayoutEffect } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import cs from "classnames";
import Message from "./Message";
import { UserType } from "../../../types/userType";
import { Bell } from "../../Icons/general-interface-2";
import { Text } from "../../Type";
import { useStoreContext } from "../../../contexts/StoreProvider";
import styles from "./index.module.scss";

const GET_USER_NOTIFICATIONS = gql`
  query Notifications($id: ID!) {
    notifications(
      sort: "created_at:DESC"
      limit: 10
      where: { recipients: { id: $id } }
    ) {
      message
      id
      created_at
      recipients {
        id
      }
      author {
        first_name
        last_name
        role {
          name
        }
      }
    }
  }
`;

const UPDATE_NOTIFICATION = gql`
  mutation UpdateNotification($input: updateNotificationInput) {
    updateNotification(input: $input) {
      notification {
        message
        id
        created_at
        recipients {
          id
        }
        author {
          first_name
          last_name
          role {
            name
          }
        }
      }
    }
  }
`;

type NotificationType = {
  author: UserType;
  id: string;
  message: string;
  created_at: string;
  recipients: { id: string }[];
};

const Notification = () => {
  const {
    state: { user },
  } = useStoreContext();
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [newNotifications, setNewNotifications] = useState(false);
  const [, setBoundaryError] = useState<Error | null>(null);
  const { data, refetch } = useQuery(GET_USER_NOTIFICATIONS, {
    variables: {
      id: user?.id,
    },
    skip: !user,
    onCompleted: (data) => {
      // red bubble should be on until user deletes all notifications. Deleting notification
      // means that user read. Asana: https://app.asana.com/0/1200279486914563/1201392371388317
      const savedNotifications = data.notifications.map(
        (n: NotificationType) => n.id
      );
      if (savedNotifications && savedNotifications.length > 0) {
        setNewNotifications(true);
      } else {
        setNewNotifications(false);
      }
    },
    pollInterval: 30000,
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      // Don't crash the page for notification errors
      if (error.message === "Forbidden access") return;
      console.error("Notification query error:", error.message);
    },
  });

  const [updateNotification] = useMutation(UPDATE_NOTIFICATION, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleHideDropdown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      buttonRef &&
      buttonRef.current &&
      (buttonRef.current.contains(target) || buttonRef.current === target)
    ) {
      return;
    }
    if (ref && ref.current && !ref.current.contains(target)) {
      setIsOpen(false);
    }
  };

  const handleMessageClose = (id: string, data: NotificationType) => {
    const recipients = data.recipients
      .filter((rec) => rec.id !== user?.id)
      .map((obj) => obj.id);
    updateNotification({
      variables: {
        input: {
          data: {
            recipients,
          },
          where: {
            id,
          },
        },
      },
    }).catch((error) => {
      setBoundaryError(() => {
        throw error;
      });
    });
  };

  useLayoutEffect(() => {
    document.addEventListener("keydown", handleHideDropdown, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("keydown", handleHideDropdown, true);
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  return (
    <div className={styles.container}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className={styles.button}
      >
        <Bell width="24px" height="24px" />
        {newNotifications && (
          <div className={styles.marker}>
            <span className={styles.markerAlert} />
          </div>
        )}
      </button>
      <div ref={ref} className={cs(styles.dropdown, isOpen && styles.open)}>
        <div className={styles.header}>
          <Text size="large" weight="bold">
            Notifications
          </Text>
          {data && data.notifications && (
            <span className={styles.count}>{data.notifications.length}</span>
          )}
        </div>
        <div className={styles.messages}>
          {data && data.notifications && data.notifications.length > 0 ? (
            <>
              {data.notifications.map((notification: NotificationType) => (
                <Message
                  key={notification.id}
                  author={notification.author}
                  message={notification.message}
                  date={notification.created_at}
                  onClose={() =>
                    handleMessageClose(notification.id, notification)
                  }
                />
              ))}
            </>
          ) : (
            <Text className={styles.empty}>No notifications</Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notification;
